import Repository from './repository';
import { useSettingsStore } from '@/stores/settings';
import { pinia } from '@/stores/pinia';

const resource = '/userpreference';
const parseUIPreferences = (ui) => {
	if (!ui) {
		return null;
	}

	if (typeof ui === 'string') {
		try {
			return JSON.parse(ui);
		} catch {
			return null;
		}
	}

	if (typeof ui === 'object') {
		return ui;
	}

	return null;
};

const parseUIPreferencesFromUserPreference = (userPreference) =>
	parseUIPreferences(userPreference?.uiPreferences ?? userPreference?.ui);

const extractUserPreference = (responseData) => {
	const preference = responseData?.data?.preference;
	if (!preference || typeof preference !== 'object') {
		return null;
	}

	const userPreference =
		preference.userPreference && typeof preference.userPreference === 'object'
			? preference.userPreference
			: preference;

	return userPreference && typeof userPreference === 'object'
		? userPreference
		: null;
};

const parseSplitViewEnabled = (uiPreferences) => {
	const value = uiPreferences?.splitViewEnabled ?? uiPreferences?.splitView;
	if (typeof value === 'boolean') {
		return value;
	}
	if (value === 'true') {
		return true;
	}
	if (value === 'false') {
		return false;
	}
	return undefined;
};

export default {
	getConstants() {
		return Repository.getWithAuth(`${resource}/constants`);
	},

	getPreferences(userId) {
		return Repository.getWithAuth(`${resource}/${userId}`);
	},

	getPreferencesAndSave(userId) {
		return this.getPreferences(userId).then(
			(resp) => {
				if (resp.data.success) {
					const userPreference = extractUserPreference(resp.data);
					if (!userPreference) {
						return;
					}
					const uiPreferences =
						parseUIPreferencesFromUserPreference(userPreference);
					if (uiPreferences) {
						useSettingsStore(pinia).applyUIPreferences(uiPreferences);
					}
					let dateFormat = userPreference.dateFormat;
					let nameFormat = userPreference.nameFormat;
					let timeZone =
						userPreference.timeZone?.replace(/ \(.*\)/, '') || 'UTC';
					let clockFormat = userPreference.clockFormat;
					let colorMode = uiPreferences?.colorMode || userPreference.colorMode;
					const splitViewEnabled = parseSplitViewEnabled(uiPreferences);
					this.saveUserPreferencesInLocalStorage(
						timeZone,
						dateFormat,
						nameFormat,
						clockFormat,
						colorMode,
						splitViewEnabled
					);
				}
			},
			(err) => {
				console.log(err);
			}
		);
	},

	saveUserPreferencesInLocalStorage(
		timeZone,
		dateFormat,
		nameFormat,
		clockFormat,
		colorMode,
		splitViewEnabled
	) {
		localStorage.setItem('preferredDateFormat', dateFormat);
		localStorage.setItem('preferredNameFormat', nameFormat);
		localStorage.setItem('preferredTimeZone', timeZone);
		localStorage.setItem('preferredClockFormat', clockFormat);
		if (colorMode === 'light' || colorMode === 'dark') {
			localStorage.setItem('settings.colorMode', colorMode);
			if (typeof document !== 'undefined') {
				document.documentElement.setAttribute('data-color-mode', colorMode);
			}
		}
		if (typeof splitViewEnabled === 'boolean') {
			localStorage.setItem(
				'settings.splitViewEnabled',
				String(splitViewEnabled)
			);
		}
	},

	upsertPreferences(payload) {
		const normalizedPayload = {
			...(payload || {}),
		};

		const rawUIPreferences =
			normalizedPayload.uiPreferences ?? normalizedPayload.ui ?? {};
		normalizedPayload.uiPreferences =
			typeof rawUIPreferences === 'string'
				? rawUIPreferences
				: JSON.stringify(rawUIPreferences || {});
		delete normalizedPayload.ui;

		return Repository.postWithAuth(`${resource}/upsert`, normalizedPayload);
	},
};
